/* Dialogen voor de status: verstuurd (dag en ontvanger, met herinnering na 7 dagen)
 * en het besluit van de klant (geaccepteerd of afgewezen, met dag en toelichting). */

import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { HERINNER_NA_DAGEN, lokaleDag, plusDagen } from '../data/status';
import { datum } from '../../projectpagina/ui/opmaak';

export function VerstuurdDialoog({
  open, onOpenChange, standaardAan, revisie, opnieuw, waarschuwing, bezig, onBevestig,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  standaardAan: string;
  revisie: number | null;
  /** Al verstuurd: dit is een nieuwe verzending (bijvoorbeeld een nieuwe revisie). */
  opnieuw: boolean;
  /** Bijvoorbeeld: deze revisie staat nog niet als PDF in de projectmap. */
  waarschuwing?: string | null;
  bezig: boolean;
  onBevestig: (v: { op: string; aan: string }) => void;
}) {
  const [op, setOp] = useState(lokaleDag());
  const [aan, setAan] = useState(standaardAan);
  useEffect(() => {
    if (open) {
      setOp(lokaleDag());
      setAan(standaardAan);
    }
  }, [open, standaardAan]);
  const geldig = /^\d{4}-\d{2}-\d{2}$/.test(op) && aan.trim().length >= 2;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{opnieuw ? 'Opnieuw verstuurd' : 'Markeer als verstuurd'}</DialogTitle>
          <DialogDescription>
            Het CRM verstuurt zelf niets: dit legt alleen vast dat jij de offerte verstuurd hebt.{' '}
            {revisie ? `Revisie ${revisie}. ` : ''}Er komt een herinnertaak voor je op {geldig ? datum(plusDagen(op, HERINNER_NA_DAGEN)) : '…'}
            {opnieuw ? '; de vorige herinnering vervalt.' : '.'}
          </DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (geldig) onBevestig({ op, aan: aan.trim() });
          }}
        >
          {waarschuwing && (
            <p className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">{waarschuwing}</p>
          )}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="verstuurd-aan">Aan</Label>
            <Input id="verstuurd-aan" value={aan} onChange={(e) => setAan(e.target.value)} placeholder="Naam en e-mailadres" autoFocus />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="verstuurd-op">Verstuurd op</Label>
            <Input id="verstuurd-op" type="date" value={op} max={lokaleDag()} onChange={(e) => setOp(e.target.value)} className="w-44" />
          </div>
          <DialogFooter className="mt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuleren</Button>
            <Button type="submit" disabled={!geldig || bezig}>{bezig ? 'Bezig…' : 'Verstuurd'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function BesluitDialoog({
  open, uitkomst, onOpenChange, bezig, onBevestig,
}: {
  open: boolean;
  /** Blijft staan tijdens het sluiten, zodat de titel niet omslaat. */
  uitkomst: 'accepted' | 'declined';
  onOpenChange: (o: boolean) => void;
  bezig: boolean;
  onBevestig: (b: { op: string; notitie: string }) => void;
}) {
  const [op, setOp] = useState(lokaleDag());
  const [notitie, setNotitie] = useState('');
  useEffect(() => {
    if (open) {
      setOp(lokaleDag());
      setNotitie('');
    }
  }, [open]);
  const akkoord = uitkomst === 'accepted';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{akkoord ? 'Geaccepteerd door de klant' : 'Afgewezen door de klant'}</DialogTitle>
          <DialogDescription>De herinnertaak voor deze offerte wordt afgerond.</DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            onBevestig({ op, notitie });
          }}
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="besluit-op">{akkoord ? 'Geaccepteerd op' : 'Afgewezen op'}</Label>
            <Input id="besluit-op" type="date" value={op} max={lokaleDag()} onChange={(e) => setOp(e.target.value)} className="w-44" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="besluit-notitie">{akkoord ? 'Toelichting (optioneel)' : 'Reden (optioneel)'}</Label>
            <Textarea
              id="besluit-notitie"
              rows={3}
              value={notitie}
              onChange={(e) => setNotitie(e.target.value)}
              placeholder={akkoord ? 'Bijvoorbeeld: getekend retour, start in november' : 'Bijvoorbeeld: te duur, kiest voor huur'}
            />
          </div>
          <DialogFooter className="mt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuleren</Button>
            <Button type="submit" disabled={bezig || !/^\d{4}-\d{2}-\d{2}$/.test(op)} variant={akkoord ? 'default' : 'destructive'}>
              {bezig ? 'Bezig…' : akkoord ? 'Geaccepteerd' : 'Afgewezen'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
